import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './App.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Loading from './components/Loading';

// Lazy load components for code splitting and smaller initial bundle
const Landing = lazy(() => import('./components/Landing'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const Explore = lazy(() => import('./components/Explore'));
const Resume = lazy(() => import('./components/Resume'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const DSASalarySections = lazy(() => import('./components/DSASalarySections'));
const ProblemDetail = lazy(() => import('./components/ProblemDetail'));
const AddProblem = lazy(() => import('./components/AddProblem'));
const ManageTopics = lazy(() => import('./components/ManageTopics'));
const Profile = lazy(() => import('./components/Profile'));
const UserDetail = lazy(() => import('./components/UserDetail'));

// Loading fallback component
const LoadingFallback = () => <Loading />;

const NotFound = () => {
  const navigate = useNavigate();
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>404 - Page Not Found</h1>
      <p>The page you're looking for doesn't exist.</p>
      <button onClick={() => navigate('/')}>Go Home</button>
    </div>
  );
};

// Scroll restoration component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const prevPathname = React.useRef('');

  useEffect(() => {
    // Track previous route in sessionStorage for other components to use
    const previousRoute = prevPathname.current || sessionStorage.getItem('currentRoute') || '';
    sessionStorage.setItem('previousRoute', previousRoute);
    sessionStorage.setItem('currentRoute', pathname);

    // Save scroll position when leaving dashboard
    if (prevPathname.current === '/user/dashboard' && pathname !== '/user/dashboard') {
      sessionStorage.setItem('dashboardScrollPosition', window.scrollY.toString());
    }

    // Restore scroll position when returning to dashboard
    if (pathname === '/user/dashboard') {
      const savedPosition = sessionStorage.getItem('dashboardScrollPosition');
      if (savedPosition) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedPosition, 10),
            behavior: 'instant'
          });
        }, 100);
      } else {
        // If no saved position, scroll to top
        window.scrollTo(0, 0);
      }
    } else if (prevPathname.current !== pathname) {
      // For other pages, scroll to top on navigation
      window.scrollTo(0, 0);
    }

    // Update previous pathname
    prevPathname.current = pathname;
  }, [pathname]);

  return null;
};

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const isProfilePage = location.pathname === '/user/profile';
  const isUserDetailPage = location.pathname.startsWith('/admin/users/');
  const isPublicPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/explore' || location.pathname === '/resume' || location.pathname === '/leaderboard';

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="App">
      <ScrollToTop />
      {isAuthenticated && !isPublicPage && <Header />}
      <div className={isAuthenticated && !isPublicPage ? "main-container" : ""}>
        {isAuthenticated && !isProfilePage && !isUserDetailPage && !isPublicPage && <Sidebar />}
        <div className={isAuthenticated && !isPublicPage ? (isProfilePage || isUserDetailPage ? "content-area full-width" : "content-area") : ""}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route 
                path="/" 
                element={<Landing />} 
              />
              <Route 
                path="/login" 
                element={isAuthenticated ? <Navigate to="/user/dashboard" replace /> : <Login />} 
              />
              <Route 
                path="/register" 
                element={isAuthenticated ? <Navigate to="/user/dashboard" replace /> : <Register />} 
              />
              <Route 
                path="/explore" 
                element={<Explore />} 
              />
              <Route 
                path="/resume" 
                element={<Resume />} 
              />
              <Route 
                path="/leaderboard" 
                element={<Leaderboard />} 
              />
              
              {/* Protected routes - Admin */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/add-problem"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AddProblem />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/manage-topics"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <ManageTopics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:id"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <UserDetail />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected routes - User */}
              <Route
                path="/user/dashboard"
                element={
                  <ProtectedRoute>
                    <UserDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/dsa/salary-sections"
                element={
                  <ProtectedRoute>
                    <DSASalarySections />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem/:id"
                element={
                  <ProtectedRoute>
                    <ProblemDetail />
                  </ProtectedRoute>
                }
              />
              
              {/* Catch-all route for 404 */}
              <Route
                path="*"
                element={<NotFound />}
              />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;

